import { useContext } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  asOperatorDescriptor,
  buildProductionCriteriaCellButton,
  buildProductionCriteriaCustomProduceButton,
  buildProductionCriteriaSummaryItems,
  DatesUnit,
  IBookingsProductionDrawerConfigurationForm,
  IProduceButtonDto,
  IProductionSummaryItem,
  parseBoolean,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { DropdownSourceContext } from "../../contexts";
import { useSelectItemsTranslate } from "../use-select-items-translate";

export const useProduceSummary = () => {
  const { t } = useTranslation();
  const { translateLabel } = useSelectItemsTranslate();
  const { formatDateTimeNormalized } = useFormats();
  const { getDropdownLabels } = useContext(DropdownSourceContext);
  const { timezone } = useTimezone();

  const summaryDeps = {
    t,
    translateLabel,
    formatDateTimeNormalized,
    getDropdownLabels,
  };

  const buildProduceSummaryForCustomProductionDialog = useEvent(
    async (
      customProductionFormData: IBookingsProductionDrawerConfigurationForm,
      bookingDates?: DatesUnit,
    ): Promise<IProductionSummaryItem[]> => {
      const criteria = buildProductionCriteriaCustomProduceButton({
        ...customProductionFormData,
        direction: customProductionFormData.direction!,
        produceFrom:
          customProductionFormData.produceFrom ??
          bookingDates?.date1 ??
          undefined,
        produceTo:
          customProductionFormData.produceTo ??
          bookingDates?.date2 ??
          undefined,
      } as Parameters<typeof buildProductionCriteriaCustomProduceButton>[0]);

      return buildProductionCriteriaSummaryItems(
        {
          criteria,
          producedFrom:
            customProductionFormData.produceFrom ?? bookingDates?.date1,
          producedTo: customProductionFormData.produceTo ?? bookingDates?.date2,
          rideTypeId: customProductionFormData.rideTypeId,
          productionType: customProductionFormData.productionType,
          productionAction: customProductionFormData.productionAction,
          directionsService: customProductionFormData.directionsService,
          assignDocksDuringProduction:
            customProductionFormData.assignDocksDuringProduction,
          doNotLockBookings: parseBoolean(
            customProductionFormData.doNotLockBookings,
          ),
        },
        summaryDeps,
      );
    },
  );

  const buildProduceSummaryForCellButton = useEvent(
    async (
      produceButtonDto: Partial<IProduceButtonDto>,
    ): Promise<IProductionSummaryItem[]> => {
      const criteria = buildProductionCriteriaCellButton(
        produceButtonDto as IProduceButtonDto,
        timezone,
      );
      const bookTimeRange = asOperatorDescriptor(criteria.bookTime)
        ?.$between as [TDateISO?, TDateISO?] | undefined;

      return buildProductionCriteriaSummaryItems(
        {
          criteria,
          name: produceButtonDto.name,
          producedFrom: bookTimeRange?.[0],
          producedTo: bookTimeRange?.[1],
          rideTypeId: produceButtonDto.rideTypeId,
          rideTypeName: produceButtonDto.rideTypeName,
          productionType: produceButtonDto.productionType,
          productionAction: produceButtonDto.productionAction,
          directionsService: produceButtonDto.metadata?.directionsService,
          assignDocksDuringProduction:
            produceButtonDto.metadata?.assignDocksDuringProduction,
          sendToSupplierAfterProduce: parseBoolean(
            produceButtonDto.metadata?.sendToSupplierAfterProduce,
          ),
          doNotLockBookings: parseBoolean(
            produceButtonDto.metadata?.doNotLockBookings,
          ),
          dayTypeIds: produceButtonDto.dayTypeIds,
        },
        summaryDeps,
      );
    },
  );

  return {
    buildProduceSummaryForCustomProductionDialog,
    buildProduceSummaryForCellButton,
  };
};
